/**
 * Lead Comms — reverse-chronological communication history on the lead workspace.
 * Types: call, meeting, email, note. Staff can add / edit / delete; filter by type.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Mail,
  MessageSquare,
  NotebookPen,
  Pencil,
  Phone,
  Plus,
  Trash2,
  Video,
  X,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import type { LeadComm, LeadCommAudience, LeadCommType } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import {
  createLeadComm,
  deleteLeadComm,
  subscribeLeadComms,
  updateLeadComm,
} from '../../utils/leadCommsService';
import './LeadCommsSection.css';

export const EXPAND_LEAD_COMMS_EVENT = 'lctf:expandLeadComms';

const VALID_TYPES: LeadCommType[] = ['call', 'meeting', 'email', 'note'];

const TYPE_META: Record<
  LeadCommType,
  { label: string; Icon: typeof Phone }
> = {
  call: { label: 'Call', Icon: Phone },
  meeting: { label: 'Meeting', Icon: Video },
  email: { label: 'Email', Icon: Mail },
  note: { label: 'Note', Icon: NotebookPen },
};

type TypeFilter = 'all' | LeadCommType;

export interface LeadCommsSectionProps {
  leadId: string;
  /** When true, hide add/edit/delete (e.g. Project Hub read-along). */
  readOnly?: boolean;
  /** Optional heading override (default "Comms"). */
  title?: string;
  /** Start expanded (default false on lead page; true when embedded in Project Hub). */
  defaultExpanded?: boolean;
  /** Use an external list instead of subscribing by leadId (Project Hub merge). */
  items?: LeadComm[];
}

function summaryPreview(text: string, maxLines = 3): string {
  const lines = (text || '').trim().split(/\n+/).filter(Boolean);
  if (lines.length <= maxLines) return lines.join('\n');
  return `${lines.slice(0, maxLines).join('\n')}…`;
}

function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(raw: string): Date {
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

type FormState = {
  type: LeadCommType;
  occurredAt: string;
  title: string;
  summary: string;
  participants: string;
  sourceLink: string;
  audience: LeadCommAudience;
};

const emptyForm = (): FormState => ({
  type: 'note',
  occurredAt: toDatetimeLocalValue(new Date()),
  title: '',
  summary: '',
  participants: '',
  sourceLink: '',
  audience: 'internal',
});

function formFromComm(c: LeadComm): FormState {
  return {
    type: c.type,
    occurredAt: toDatetimeLocalValue(c.occurredAt),
    title: c.title,
    summary: c.summary,
    participants: (c.participants || []).join(', '),
    sourceLink: c.sourceLink || '',
    audience: c.audience,
  };
}

const LeadCommsSection: React.FC<LeadCommsSectionProps> = ({
  leadId,
  readOnly = false,
  title = 'Comms',
  defaultExpanded = false,
  items,
}) => {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [comms, setComms] = useState<LeadComm[]>(items || []);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onExpand = () => setExpanded(true);
    window.addEventListener(EXPAND_LEAD_COMMS_EVENT, onExpand);
    return () => window.removeEventListener(EXPAND_LEAD_COMMS_EVENT, onExpand);
  }, []);

  useEffect(() => {
    if (items) {
      setComms(items);
      return;
    }
    if (!leadId) {
      setComms([]);
      return;
    }
    return subscribeLeadComms(
      leadId,
      rows => {
        setComms(rows);
        setError(null);
      },
      e => setError(e.message || 'Could not load comms'),
    );
  }, [leadId, items]);

  const filtered = useMemo(() => {
    if (typeFilter === 'all') return comms;
    return comms.filter(c => c.type === typeFilter);
  }, [comms, typeFilter]);

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (c: LeadComm) => {
    if (!c.id) return;
    setEditingId(c.id);
    setForm(formFromComm(c));
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleSave = async () => {
    if (!user?.id || readOnly) return;
    const titleTrim = form.title.trim();
    if (!titleTrim) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const participants = form.participants
        .split(/[,;]/)
        .map(s => s.trim())
        .filter(Boolean);
      const payload = {
        type: form.type,
        occurredAt: fromDatetimeLocalValue(form.occurredAt),
        title: titleTrim,
        summary: form.summary,
        participants,
        sourceLink: form.sourceLink.trim() || undefined,
        audience: form.audience,
      };
      if (editingId) {
        await updateLeadComm(editingId, payload);
      } else {
        await createLeadComm({
          leadId,
          ...payload,
          source: 'manual',
          createdBy: user.id,
        });
      }
      closeForm();
    } catch (e) {
      setError((e as Error).message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c: LeadComm) => {
    if (!c.id || readOnly) return;
    if (!window.confirm(`Delete “${c.title}”?`)) return;
    try {
      await deleteLeadComm(c.id);
    } catch (e) {
      setError((e as Error).message || 'Failed to delete');
    }
  };

  const summaryLine = `${comms.length} entr${comms.length === 1 ? 'y' : 'ies'}`;

  return (
    <div
      className={`lcs-root ${expanded ? 'expanded' : 'collapsed'}`}
      data-tour="cw-lead-comms"
    >
      <div className="lcs-toggle">
        <button
          type="button"
          className="lcs-toggle-main"
          onClick={() => setExpanded(v => !v)}
          aria-expanded={expanded}
        >
          <span className="lcs-toggle-icon">
            <MessageSquare size={15} />
          </span>
          <span className="lcs-toggle-label">{title}</span>
          <span className="lcs-toggle-summary">{summaryLine}</span>
          <span className="lcs-toggle-chev">
            <ChevronDown size={16} />
          </span>
        </button>
      </div>

      {expanded && (
        <div className="lcs-body">
          <div className="lcs-toolbar">
            <div className="lcs-filters" role="group" aria-label="Filter by type">
              {(['all', 'call', 'meeting', 'email', 'note'] as TypeFilter[]).map(t => (
                <button
                  key={t}
                  type="button"
                  className={`lcs-filter${typeFilter === t ? ' lcs-filter--active' : ''}`}
                  onClick={() => setTypeFilter(t)}
                >
                  {t === 'all' ? 'All' : TYPE_META[t].label}
                </button>
              ))}
            </div>
            {!readOnly && (
              <button type="button" className="lcs-add-btn" onClick={openCreate}>
                <Plus size={14} /> Add
              </button>
            )}
          </div>

          {error && <p className="lcs-error">{error}</p>}

          {formOpen && !readOnly && (
            <div className="lcs-form" role="form" aria-label={editingId ? 'Edit comm' : 'Add comm'}>
              <div className="lcs-form-head">
                <strong>{editingId ? 'Edit entry' : 'New entry'}</strong>
                <button type="button" className="lcs-icon-btn" onClick={closeForm} aria-label="Close">
                  <X size={14} />
                </button>
              </div>
              <div className="lcs-form-grid">
                <label>
                  Type
                  <select
                    value={form.type}
                    onChange={e => setForm(f => ({ ...f, type: e.target.value as LeadCommType }))}
                  >
                    {VALID_TYPES.map(t => (
                      <option key={t} value={t}>
                        {TYPE_META[t].label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  When
                  <input
                    type="datetime-local"
                    value={form.occurredAt}
                    onChange={e => setForm(f => ({ ...f, occurredAt: e.target.value }))}
                  />
                </label>
                <label>
                  Audience
                  <select
                    value={form.audience}
                    onChange={e =>
                      setForm(f => ({ ...f, audience: e.target.value as LeadCommAudience }))
                    }
                  >
                    <option value="internal">Internal</option>
                    <option value="external">External</option>
                  </select>
                </label>
                <label className="lcs-form-span">
                  Title
                  <input
                    type="text"
                    value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="Short title"
                  />
                </label>
                <label className="lcs-form-span">
                  Participants
                  <input
                    type="text"
                    value={form.participants}
                    onChange={e => setForm(f => ({ ...f, participants: e.target.value }))}
                    placeholder="Comma-separated names"
                  />
                </label>
                <label className="lcs-form-span">
                  Source link
                  <input
                    type="url"
                    value={form.sourceLink}
                    onChange={e => setForm(f => ({ ...f, sourceLink: e.target.value }))}
                    placeholder="https://fathom.video/calls/…"
                  />
                </label>
                <label className="lcs-form-span">
                  Summary
                  <textarea
                    rows={5}
                    value={form.summary}
                    onChange={e => setForm(f => ({ ...f, summary: e.target.value }))}
                    placeholder="Markdown is fine"
                  />
                </label>
              </div>
              <div className="lcs-form-actions">
                <button type="button" className="lcs-btn-secondary" onClick={closeForm} disabled={saving}>
                  Cancel
                </button>
                <button type="button" className="lcs-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : editingId ? 'Save' : 'Add entry'}
                </button>
              </div>
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="lcs-empty">
              <p>No {typeFilter === 'all' ? '' : `${TYPE_META[typeFilter].label.toLowerCase()} `}comms yet.</p>
              {!readOnly && (
                <p className="lcs-empty-hint">
                  Log calls, paste Fathom/Granola summaries, emails, or quick notes here.
                </p>
              )}
            </div>
          ) : (
            <ul className="lcs-list" role="list">
              {filtered.map(c => {
                const id = c.id || `${c.source}-${c.externalId}-${c.occurredAt.getTime()}`;
                const meta = TYPE_META[c.type] || TYPE_META.note;
                const { Icon } = meta;
                const isOpen = expandedIds.has(id);
                const longSummary =
                  (c.summary || '').split(/\n+/).filter(Boolean).length > 3 ||
                  (c.summary || '').length > 280;

                return (
                  <li key={id} className="lcs-card" role="listitem">
                    <header className="lcs-card-head">
                      <span className={`lcs-type lcs-type--${c.type}`} title={meta.label}>
                        <Icon size={13} aria-hidden />
                        {meta.label}
                      </span>
                      <span
                        className={`lcs-audience lcs-audience--${c.audience}`}
                        title={c.audience === 'internal' ? 'Internal' : 'External'}
                      >
                        {c.audience === 'internal' ? 'Internal' : 'External'}
                      </span>
                      <div className="lcs-card-title-wrap">
                        <h4 className="lcs-card-title">{c.title}</h4>
                        <div className="lcs-card-meta">
                          <span>
                            <Calendar size={12} aria-hidden />
                            {format(c.occurredAt, 'MMM d, yyyy · h:mm a')}
                            <span className="lcs-relative">
                              {' '}
                              · {formatDistanceToNow(c.occurredAt, { addSuffix: true })}
                            </span>
                          </span>
                          {c.participants.length > 0 && (
                            <span title={c.participants.join(', ')}>
                              {c.participants.slice(0, 3).join(', ')}
                              {c.participants.length > 3 ? ` +${c.participants.length - 3}` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="lcs-card-actions">
                        {c.sourceLink ? (
                          <a
                            className="lcs-source-link"
                            href={c.sourceLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink size={13} aria-hidden />
                            Source
                          </a>
                        ) : null}
                        {!readOnly && (
                          <>
                            <button
                              type="button"
                              className="lcs-icon-btn"
                              onClick={() => openEdit(c)}
                              title="Edit"
                              aria-label="Edit"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              type="button"
                              className="lcs-icon-btn lcs-icon-btn--danger"
                              onClick={() => handleDelete(c)}
                              title="Delete"
                              aria-label="Delete"
                            >
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    </header>

                    {c.summary ? (
                      <div className="lcs-summary">
                        <p
                          className={
                            isOpen
                              ? 'lcs-summary-body'
                              : 'lcs-summary-body lcs-summary-body--clamped'
                          }
                        >
                          {isOpen ? c.summary : summaryPreview(c.summary)}
                        </p>
                        {longSummary && (
                          <button
                            type="button"
                            className="lcs-summary-toggle"
                            onClick={() => toggleExpanded(id)}
                          >
                            {isOpen ? (
                              <>
                                <ChevronUp size={13} aria-hidden /> Show less
                              </>
                            ) : (
                              <>
                                <ChevronDown size={13} aria-hidden /> Show full summary
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadCommsSection;
